import { parseDeepSignUrl } from '@/util'
import { createGlobalState } from '@vueuse/core'
import { IDENT_FLOW_STATE_KEY } from '@/composables/use-ident-flow-state.ts'

const EMBED_ORIGIN_KEY = 'deep_id_embed_origin'
const EMBED_PRIMARY_COLOR_KEY = 'deep_id_embed_primary_color'
const EMBED_LAYOUT_KEY = 'deep_id_embed_layout'
const EMBED_ORIGIN_QUERY_PARAM = 'embedOrigin'
const EMBED_PRIMARY_COLOR_QUERY_PARAM = 'embedPrimaryColor'
const EMBED_LAYOUT_QUERY_PARAM = 'embedLayout'

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

export type EmbedEvent =
  | { type: 'identification-submitted' }
  | { type: 'signature-completed'; outcome: 'signed' | 'refused' }
  | { type: 'closed' }
  | { type: 'resize'; height: number }

/**
 * Embedding host (DeepSign) that framed this app, from the `embedOrigin` query
 * param of the landing URL, plus its current (possibly org-branded) primary color.
 */
export const useEmbed = createGlobalState(() => {
  const embedOrigin = useSessionStorage(EMBED_ORIGIN_KEY, '')
  const primaryColor = useSessionStorage(EMBED_PRIMARY_COLOR_KEY, '')
  const layout = useSessionStorage(EMBED_LAYOUT_KEY, '')

  const query = new URLSearchParams(window.location.search)
  const requestedOrigin = query.get(EMBED_ORIGIN_QUERY_PARAM)
  if (requestedOrigin !== null) {
    embedOrigin.value = parseDeepSignUrl(requestedOrigin)?.origin ?? ''

    const requestedColor = query.get(EMBED_PRIMARY_COLOR_QUERY_PARAM)?.trim()
    primaryColor.value =
      requestedColor && HEX_COLOR.test(requestedColor) ? requestedColor : ''

    layout.value = query.get(EMBED_LAYOUT_QUERY_PARAM) === 'fill' ? 'fill' : ''

    // each DeepSign modal loads a fresh frame, but session storage outlives it; a flow abandoned in an earlier modal must not resume
    if (embedOrigin.value) sessionStorage.removeItem(IDENT_FLOW_STATE_KEY)
  }

  const isEmbedded = computed(
    () => !!embedOrigin.value && window.parent !== window,
  )

  const embedPrimaryColor = computed(() =>
    isEmbedded.value && primaryColor.value ? primaryColor.value : undefined,
  )

  // `fill`: the host (a fullscreen dialog on phones) gives the frame a fixed height, so the regular mobile layout applies
  const fitsContent = computed(
    () => isEmbedded.value && layout.value !== 'fill',
  )

  function notifyParent(event: EmbedEvent) {
    if (!isEmbedded.value) return

    window.parent.postMessage({ source: 'deepid', ...event }, embedOrigin.value)
  }

  /** Lets the embedding modal size its iframe to `content` instead of a fixed height. */
  function reportContentHeight(content: HTMLElement) {
    if (!fitsContent.value) return

    let contentHeight = 0

    // the page may only scroll once the host caps the frame below the content; otherwise subpixel overflow shows an empty scrollbar
    function updateCapped() {
      document.documentElement.classList.toggle(
        'is-embedded-capped',
        window.innerHeight < contentHeight,
      )
    }

    useResizeObserver(content, () => {
      // offsetHeight rounds, so a fractional height could come out a pixel short and make the frame scroll
      contentHeight = Math.ceil(content.getBoundingClientRect().height)
      notifyParent({ type: 'resize', height: contentHeight })
      updateCapped()
    })
    useEventListener(window, 'resize', updateCapped)
  }

  return {
    isEmbedded,
    fitsContent,
    embedPrimaryColor,
    notifyParent,
    reportContentHeight,
  }
})
